import { Component, OnInit } from '@angular/core';
import { RouterOutlet, RouterLink, RouterLinkActive } from '@angular/router';
import { HttpClient } from '@angular/common/http';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { AuthServiceService } from '../../services/auth-service.service';
import { SsoAuthService } from '../../../SsoAuth/Service/sso-auth.service';
import { ROUTES } from '../../common-files/enums/server-routes';
import { RequestMapper } from '../../request-mapper';
import {
  parseEstablishmentId,
  parseStoredPermissionTree,
  PermissionView,
  serializePermissionSource,
} from '../../common-files/permission-tree';
import { DEFAULT_SIDE_NAV_ICON, resolveSideNavIcon } from './side-nav-icons';
import { ContextService } from '../../services/context/context.service';
import { ViewAsService, ViewAsState } from '../../services/view-as/view-as.service';
import { Router } from '@angular/router';

interface SideNavItem {
  label: string;
  route?: string;
  icon: string;
  children: SideNavItem[] | null;
}

@Component({
  selector: 'app-side-nav',
  standalone: true,
  imports: [
    RouterOutlet,
    RouterLink,
    RouterLinkActive,
    MatFormFieldModule,
    MatSelectModule,
  ],
  templateUrl: './side-nav.component.html',
  styleUrl: './side-nav.component.scss',
})
export class SideNavComponent implements OnInit {
  constructor(
    private authService: AuthServiceService,
    private ssoService: SsoAuthService,
    private http: HttpClient,
    private contextService: ContextService,
    private viewAsService: ViewAsService,
    private router: Router,
  ) {}

  // Super Admin viewing the app as a role (null otherwise)
  viewAs: ViewAsState | null = null;

  name: string | null = null;
  roleName: string | null = null;
  contextLabel: string | null = null;
  isSidebarOpen = false;

  // branch picker — only shown when the operator can act on more than one branch
  // (an all-branch Establishment Central Admin). Switching sets the active branch
  // used by the x-establishment-id header on every request.
  establishments: any[] = [];
  activeEstablishmentId: number | null = null;
  showBranchPicker = false;
  canChangeContext = false;
  isBranchSwitching = false;
  readonly contextSelectRoute = RequestMapper.SELECT_CONTEXT;

  toggleSidebar() {
    this.isSidebarOpen = !this.isSidebarOpen;
  }

  menuItems: SideNavItem[] = [];
  ngOnInit(): void {
    this.viewAs = this.viewAsService.current();
    this.name = localStorage.getItem('fullName');
    this.roleName = localStorage.getItem('roleName');
    this.loadBranches();
    this.menuItems = this.buildMenu();
    // the sidebar property picker is gone (the check-in/check-out page filters by
    // property itself now); clear any value an older session left behind.
    localStorage.removeItem('activePropertyId');
    localStorage.removeItem('activePropertyName');
  }

  loadBranches(): void {
    const stored = localStorage.getItem('establishments');
    let topLevelEstablishments: any[] = [];
    try {
      const parsed = stored ? JSON.parse(stored) : [];
      topLevelEstablishments = Array.isArray(parsed) ? parsed : [];
    } catch {
      topLevelEstablishments = [];
    }

    // Some scoped login responses contain the branch list only inside the
    // permission buckets. Use that list as a compatibility fallback.
    this.establishments = topLevelEstablishments.length
      ? topLevelEstablishments
      : this.contextService.establishmentBuckets();
    this.activeEstablishmentId = parseEstablishmentId(
      localStorage.getItem('activeEstablishmentId'),
    );
    // while viewing as a role the context is fixed (Exit restores the real one);
    // the super admin's own screen has no operational screens, so no branch picker
    const isSuperAdmin = localStorage.getItem('isSuperAdmin') === 'true';
    this.showBranchPicker = !this.viewAs && !isSuperAdmin && this.establishments.length > 1;
    this.canChangeContext = !this.viewAs && !isSuperAdmin && this.contextService.needsScopeChoice();

    const activeEstablishment = this.establishments.find(
      (est) => Number(est?.establishmentId) === this.activeEstablishmentId,
    );
    const scope = localStorage.getItem('activeScope');
    this.contextLabel = scope === 'GLOBAL'
      ? 'Global'
      : localStorage.getItem('activeEstablishmentName')
        || this.establishmentNameOf(activeEstablishment)
        || null;
  }

  // Scoped sessions already contain raw permission buckets from login. Legacy
  // sessions fetch the target branch's permissions before committing context.
  switchBranch(value: string | number | null): void {
    const estId = parseEstablishmentId(value);
    const establishment = this.establishments.find(
      (item) => Number(item?.establishmentId) === estId,
    );

    if (
      estId === null
      || estId === this.activeEstablishmentId
      || !establishment
      || this.isBranchSwitching
    ) return;

    this.isBranchSwitching = true;
    if (localStorage.getItem('permissionsScoped') !== null) {
      this.switchScopedBranch(estId, establishment);
      return;
    }

    this.http.get(ROUTES.AUTH_PERMISSIONS, {
      headers: { 'x-establishment-id': String(estId) },
    }).subscribe({
      next: (res: any) => {
        if (!this.hasPermissionPayload(res?.data)) {
          this.isBranchSwitching = false;
          return;
        }

        localStorage.setItem('permissions', serializePermissionSource(res.data));
        this.commitBranchContext(estId, this.establishmentNameOf(establishment));
      },
      error: () => {
        this.isBranchSwitching = false;
      },
    });
  }

  private switchScopedBranch(estId: number, establishment: any): void {
    const buckets = this.contextService.establishmentBuckets();
    const hasEstablishmentBucket = Array.isArray(buckets) && buckets.some(
      (bucket: any) => Number(bucket?.establishmentId) === estId,
    );
    let establishmentName = this.establishmentNameOf(establishment);

    if (hasEstablishmentBucket) {
      const contextApplied = this.contextService.applyEstablishment(estId);
      if (contextApplied === false) {
        this.isBranchSwitching = false;
        return;
      }
      establishmentName = localStorage.getItem('activeEstablishmentName')
        || establishmentName;
    } else if (!this.contextService.hasGlobal()) {
      this.isBranchSwitching = false;
      return;
    } else {
      // An all-branch global operator keeps the global permission bucket while
      // choosing the establishment used by operational request headers.
      this.contextService.applyGlobal();
    }

    this.commitBranchContext(estId, establishmentName);
  }

  private commitBranchContext(estId: number, establishmentName: string): void {
    localStorage.setItem('activeEstablishmentId', String(estId));
    localStorage.setItem('activeEstablishmentName', establishmentName);
    this.activeEstablishmentId = estId;
    this.isBranchSwitching = false;
    this.reloadPage();
  }

  private hasPermissionPayload(value: any): boolean {
    if (Array.isArray(value)) return true;
    if (!value || typeof value !== 'object') return false;

    return Array.isArray(value.permissionsV2)
      || (value.permissions !== null
        && typeof value.permissions === 'object'
        && !Array.isArray(value.permissions))
      || value.global !== undefined
      || Array.isArray(value.establishments);
  }

  establishmentNameOf(establishment: any): string {
    return String(establishment?.name || establishment?.estName || '').trim();
  }

  // build the sidebar. super admin gets a fixed management menu (its screens are
  // not seeded views); everyone else is driven by the permissionsV2 tree.
  buildMenu(): SideNavItem[] {
    // super admin (not viewing as a role): only the management screens, as a flat
    // list — its screens are not seeded views. Operational screens are reached
    // through View As Role, so the catalogue is not expanded here.
    if (localStorage.getItem('isSuperAdmin') === 'true' && !this.viewAs) {
      return [
        { label: 'Users', route: RequestMapper.SA_USERS, icon: 'icons/user-line.svg', children: null },
        { label: 'Roles', route: RequestMapper.SA_ROLES, icon: 'icons/manual-update.svg', children: null },
        { label: 'Establishments', route: RequestMapper.SA_ESTABLISHMENTS, icon: 'icons/menu-img.svg', children: null },
        { label: 'Views & Permissions', route: RequestMapper.SA_VIEWS, icon: 'icons/bar-chart-box-line.svg', children: null },
        { label: 'Media', route: RequestMapper.MEDIA, icon: 'icons/menu-img.svg', children: null },
        { label: 'Platform Reports', route: RequestMapper.PLATFORM_REPORTS, icon: 'icons/report.svg', children: null },
        { label: 'View As Role', route: RequestMapper.SA_VIEW_AS_ROLE, icon: 'icons/user-line.svg', children: null },
      ];
    }
    return this.mapViews(
      parseStoredPermissionTree(
        localStorage.getItem('permissions'),
        this.activeEstablishmentId,
      ),
    );
  }

  // stop viewing as a role: restore the Super Admin's own context
  exitViewAs(): void {
    this.viewAsService.exit();
    this.router
      .navigateByUrl(`/${RequestMapper.SIDENAV}/${RequestMapper.SA_VIEW_AS_ROLE}`)
      .then(() => this.reloadPage());
  }

  mapViews(views: PermissionView[]): SideNavItem[] {
    return views
      .map((view): SideNavItem => {
        const children = view.subview.length ? this.mapViews(view.subview) : null;
        const label = typeof view.labelName === 'string'
          ? view.labelName
          : typeof view.viewName === 'string' ? view.viewName : 'Menu item';

        return {
          label,
          route: typeof view.webRoute === 'string' && view.webRoute ? view.webRoute : undefined,
          icon: resolveSideNavIcon(view),
          children,
        };
      })
      .filter((item) => Boolean(item.route || item.children?.length));
  }

  openedMenu: string | null = null;

  toggleSubMenu(item: SideNavItem): void {
    this.openedMenu = this.openedMenu === item.label ? null : item.label;
  }

  closeSidebar(): void {
    this.isSidebarOpen = false;
  }

  useFallbackIcon(event: Event): void {
    const image = event.target as HTMLImageElement;
    image.onerror = null;
    image.src = DEFAULT_SIDE_NAV_ICON;
  }

  private reloadPage(): void {
    window.location.reload();
  }

  logOut() {
    this.authService.logoutUser().subscribe({
      next: () => {
        localStorage.clear();
        this.ssoService.logOut(true);
      },
    });
  }
}
